import { gql, useMutation } from '@apollo/client'
import { STATUS, getStatusId } from '@btime/constants'
import { useNotification } from '@btime/react-messager'

import { formatApiDate } from '../../../../../utils/helpers/date'
import { generalClient, serviceOrdersClient } from '../../../../../utils/apollo/api'
import { useAlertModal } from '../../../../../utils/routes/modals/modalsActions'
import { useCloseModalOrNavigation } from '../../../../../components/modals/ModalsContext'
import { useBulkGridActions, useBulkGridRowPayload, useBulkGridRows } from '../_state/BulkGridStateContext'

// Mesma mutation usada pelo "Enviar" do rodapé (`_requests/saveServiceOrder.js`),
// só que chamada com uma única entrada — permite concluir ou pausar UMA
// atividade da grade sem esperar pelas demais.
const BULK_MUTATION = gql`
  mutation BulkExecuteServiceOrdersRow($input: BulkServiceOrderInput) {
    bulkExecuteServiceOrders(input: $input) {
      successful {
        id
      }
      failures {
        serviceOrderId
        message
      }
    }
  }
`

// Ações "Concluir" e "Pausar" por linha da grade de execução em massa.
//
// "Concluir" replica o fluxo de conclusão individual: valida obrigatórias e
// manda o evento de status COMPLETED (dispara as mesmas automações que a
// conclusão individual/da grade inteira dispararia para essa atividade).
//
// "Pausar" reaproveita o mesmo padrão da execução individual fora da grade
// (`useReasonPauseServiceOrderRequest`, em `saveAndRedirects.js`): salva as
// respostas já preenchidas SEM validar obrigatórias e manda o evento de
// status PAUSE em vez de COMPLETED — ou seja, grava o progresso sem rodar o
// fluxo de conclusão/automações de conclusão. Não existe hoje, nem na
// execução individual, uma mutation de "salvar rascunho sem tocar no status"
// dedicada — o contrato disponível distingue "salvar" de "concluir" através
// do status enviado no evento, e é esse contrato que reaproveitamos aqui.
export const useBulkRowActions = () => {
  const notify = useNotification()
  const openAlert = useAlertModal()
  const { removeRow } = useBulkGridActions()
  const getRowPayload = useBulkGridRowPayload()
  const rows = useBulkGridRows()
  const navigate = useCloseModalOrNavigation()

  const [request, state] = useMutation(BULK_MUTATION, {
    client: serviceOrdersClient,
    refetchQueries: ['ServiceOrders', 'ServiceOrder', 'ServiceOrdersCount'],
    onCompleted: () => {
      generalClient.refetchQueries({ include: ['CalendarEvents'] })
    }
  })

  const runRow = (serviceOrderId, { newStatusId, shouldValidate, successTitle, invalidTitle, invalidMessage }) => {
    const { entry, invalidReply, serviceOrder } = getRowPayload(serviceOrderId, { shouldValidate })

    if (shouldValidate && invalidReply) {
      const soLabel = serviceOrder?.yearlyId || serviceOrder?.name || `#${serviceOrderId}`
      return openAlert(invalidTitle, `${soLabel} — ${invalidReply.question?.name || ''}: ${invalidMessage}`, {
        icon: 'BsInfoCircle'
      })
    }

    if (!entry) return undefined

    const events = [{ statusId: newStatusId, eventDate: formatApiDate(new Date()) }]

    return request({ variables: { input: { entries: [entry], events } } })
      .then(resp => {
        const { successful = [], failures = [] } = resp?.data?.bulkExecuteServiceOrders || {}

        if (failures.length > 0) {
          return openAlert('Não foi possível salvar esta atividade', failures[0]?.message || 'Tente novamente.', {
            icon: 'BsInfoCircle'
          })
        }

        if (successful.length > 0) {
          notify.success({ title: successTitle })
          removeRow(serviceOrderId)

          // Se essa era a última atividade restante na grade, replica o
          // fechamento/redirecionamento que o "Enviar"/"Pausar" geral do
          // rodapé dispara ao concluir a execução em massa (ver
          // `useCompleteServiceOrderRequest`/`useReasonPauseServiceOrderRequest`
          // em `../../_requests/saveAndRedirects.js`). Sem isso, ao concluir
          // ou pausar a última linha pelos botões individuais, a grade fica
          // vazia mas o modal (fullScreen) permanece aberto — o botão "X" é
          // escondido em telas fullScreen e o clique fora está desabilitado,
          // então o usuário fica sem forma de fechar a tela.
          if (rows.length <= 1) navigate('/service-orders')
        }
      })
      .catch(() => {
        openAlert(
          'Não foi possível salvar esta atividade',
          'Ocorreu um erro ao salvar. Atualize a página e tente novamente.',
          { icon: 'BsInfoCircle' }
        )
      })
  }

  return {
    completeRow: serviceOrderId =>
      runRow(serviceOrderId, {
        newStatusId: getStatusId(STATUS.COMPLETED),
        shouldValidate: true,
        successTitle: 'Atividade concluída',
        invalidTitle: 'Resposta obrigatória pendente',
        invalidMessage: 'preencha esta pergunta antes de concluir.'
      }),
    pauseRow: serviceOrderId =>
      runRow(serviceOrderId, {
        newStatusId: getStatusId(STATUS.PAUSE),
        shouldValidate: false,
        successTitle: 'Atividade salva e pausada'
      }),
    loading: state.loading
  }
}
